/* Landing pages (sections/landing-offer.liquid, docs/60).

   <mw-landing-offer>: the pack and colour radios pick a variant from the JSON map the section
   prints ({pack: {colour: {id, a, p, u, img, t}}}). Prices arrive formatted by Liquid; this only
   swaps strings, images and the form's [data-variant-input]. Adding to the cart is quick.js's job
   (form[data-product-form]), as everywhere else in the theme.

   <mw-landing-sticky>: once the visitor has scrolled past the first screen, a card (desktop) or a
   bar (phones) with the chosen pack rides at the bottom; it hides while the buy box itself is in
   view. Its button submits the buy box's own form, so the cart drawer, errors and the «added»
   state are the same. chat-position.js lifts the chat launcher over it. */

class MwLandingOffer extends HTMLElement {
  connectedCallback() {
    try {
      this.map = JSON.parse(this.querySelector('[data-lp-map]')?.textContent || '{}');
    } catch {
      this.map = {};
    }
    this.form = this.querySelector('form[data-product-form]');
    this.perTemplate = this.dataset.perTemplate || '';
    this.addEventListener('change', (event) => {
      if (event.target.matches('.lp-choice__input, .lp-swatch__input')) this.update();
    });
    this.update();
  }

  get pack() {
    return this.querySelector('.lp-choice__input:checked')?.value || Object.keys(this.map)[0];
  }

  get colour() {
    return this.querySelector('.lp-swatch__input:checked')?.value || 'default';
  }

  // Only the exact colour counts: a pack that does not come in it has no variant, and the box says
  // so instead of quietly adding another colour (09.10: Carbon exists on O² PRO, not on the bundle).
  pick(list, colour) {
    if (!list) return null;
    return list[colour] || (colour === 'default' ? Object.values(list)[0] : null);
  }

  update() {
    const colour = this.colour;
    const variant = this.pick(this.map[this.pack], colour);
    if (!this.form) return;

    const input = this.form.querySelector('[data-variant-input]');
    if (input) input.value = variant ? variant.id : '';

    // Every card follows the colour: its price, price per night and thumbnail, or «not in this colour».
    for (const [key, list] of Object.entries(this.map)) {
      const v = this.pick(list, colour);
      const card = this.querySelector(`.lp-choice__input[value="${key}"]`)?.closest('.lp-choice');
      card?.classList.toggle('is-missing', !v);
      const missing = card?.querySelector('[data-lp-missing]');
      if (missing) {
        missing.hidden = !!v;
        missing.textContent = v ? '' : (this.dataset.missingTemplate || '').replace('%%COLOUR%%', colour);
      }
      if (!v) continue;
      const price = this.querySelector(`[data-lp-price="${key}"]`);
      if (price) price.textContent = v.p;
      const per = this.querySelector(`[data-lp-per="${key}"]`);
      if (per && v.u && this.perTemplate) per.textContent = this.perTemplate.replace('%%PRICE%%', v.u);
      const thumb = this.querySelector(`[data-lp-thumb="${key}"]`);
      if (thumb && v.t && thumb.dataset.current !== v.t) {
        thumb.removeAttribute('srcset');
        thumb.src = v.t;
        thumb.dataset.current = v.t;
      }
    }

    const name = this.querySelector('[data-lp-colour-name]');
    if (name) name.textContent = colour === 'default' ? '' : colour;

    // The product image in the gallery (only when the merchant chose no photos) follows the pick.
    const img = this.querySelector('[data-lp-img]');
    if (img && variant?.img && img.dataset.current !== variant.img) {
      if (img.dataset.current) {
        img.removeAttribute('srcset');
        img.src = variant.img;
      }
      img.dataset.current = variant.img;
    }

    const button = this.form.querySelector('.lp-offer__add');
    const label = this.form.querySelector('[data-lp-add-label]');
    const addPrice = this.form.querySelector('[data-lp-add-price]');
    const buyable = !!variant && variant.a;
    if (button) button.disabled = !buyable;
    if (label) {
      label.textContent = buyable ? label.dataset.add
        : variant ? label.dataset.soldOut
        : (this.dataset.missingTemplate || '').replace('%%COLOUR%%', colour);
    }
    if (addPrice) {
      addPrice.textContent = variant ? variant.p : '';
      addPrice.hidden = !variant;
    }

    const choiceName = this.querySelector('.lp-choice__input:checked')
      ?.closest('.lp-choice')
      ?.querySelector('.lp-choice__name')?.firstChild?.textContent?.trim();
    this.dispatchEvent(new CustomEvent('mw:landing:pick', {
      bubbles: true,
      detail: { name: choiceName, colour: colour === 'default' ? '' : colour, price: variant?.p, thumb: variant?.t, available: buyable },
    }));
  }
}

class MwLandingSticky extends HTMLElement {
  connectedCallback() {
    this.target = document.getElementById(this.dataset.target);
    this.form = document.getElementById(this.dataset.form);
    if (!this.target || !this.form) return;
    this.hidden = false;
    this.inert = true;
    this.boxInView = false;

    this.observer = new IntersectionObserver((entries) => {
      this.boxInView = entries.some((entry) => entry.isIntersecting);
      this.toggle();
    });
    this.observer.observe(this.target.querySelector('.lp-offer__form') || this.target);

    this.onScroll = () => this.toggle();
    window.addEventListener('scroll', this.onScroll, { passive: true });

    this.onPick = (event) => this.show(event.detail);
    this.target.addEventListener('mw:landing:pick', this.onPick);
    // The offer may have announced its first pick before this element was upgraded.
    this.target.querySelector('mw-landing-offer')?.update?.();

    this.querySelector('[data-lp-sticky-add]')?.addEventListener('click', () => this.form.requestSubmit());
    this.toggle();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    window.removeEventListener('scroll', this.onScroll);
    this.target?.removeEventListener('mw:landing:pick', this.onPick);
  }

  show({ name, colour, price, thumb, available }) {
    const nameEl = this.querySelector('[data-lp-sticky-name]');
    const priceEl = this.querySelector('[data-lp-sticky-price]');
    const img = this.querySelector('[data-lp-sticky-img]');
    const button = this.querySelector('[data-lp-sticky-add]');
    if (nameEl && name) nameEl.textContent = colour ? `${name} · ${colour}` : name;
    if (priceEl && price) priceEl.textContent = price;
    if (img && thumb && img.dataset.current !== thumb) {
      img.removeAttribute('srcset');
      img.src = thumb;
      img.dataset.current = thumb;
    }
    if (button) button.disabled = available === false;
  }

  toggle() {
    const shown = !this.boxInView && window.scrollY > window.innerHeight * 0.8;
    if (shown === this.classList.contains('is-shown')) return;
    this.classList.toggle('is-shown', shown);
    this.inert = !shown;
    // chat-position.js listens for scroll and resize; a bar that slides in needs one more look.
    window.dispatchEvent(new Event('resize'));
  }
}

if (!customElements.get('mw-landing-offer')) customElements.define('mw-landing-offer', MwLandingOffer);
if (!customElements.get('mw-landing-sticky')) customElements.define('mw-landing-sticky', MwLandingSticky);
